<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { KANBAN_TEMPLATES } from './constants/templates';

const props = defineProps({
  boardId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();
const applyingKey = ref(null);

const applyTemplate = async template => {
  applyingKey.value = template.key;
  try {
    // eslint-disable-next-line no-restricted-syntax
    for (const title of template.lists) {
      // Sequential, not Promise.all: each create relies on the backend's
      // before_create position callback picking up the previous list's position.
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('kanban/lists/create', {
        kanban_board_id: props.boardId,
        title,
      });
    }
    useAlert(t('KANBAN.TEMPLATE_PICKER.APPLY_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.TEMPLATE_PICKER.APPLY_ERROR'));
  } finally {
    applyingKey.value = null;
  }
};
</script>

<template>
  <div
    class="flex flex-col items-center justify-center gap-6 py-16 px-4 max-w-2xl mx-auto text-center"
  >
    <div>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('KANBAN.TEMPLATE_PICKER.TITLE') }}
      </h2>
      <p class="text-body-main text-n-slate-11">
        {{ t('KANBAN.TEMPLATE_PICKER.DESCRIPTION') }}
      </p>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
      <button
        v-for="template in KANBAN_TEMPLATES"
        :key="template.key"
        data-test-id="kanban-template-option"
        type="button"
        class="flex flex-col items-start gap-1 p-4 rounded-xl border border-n-weak text-left hover:bg-n-solid-2 disabled:opacity-50"
        :disabled="applyingKey !== null"
        @click="applyTemplate(template)"
      >
        <span class="text-heading-4 text-n-slate-12">
          {{ t(`KANBAN.TEMPLATE_PICKER.${template.key}.TITLE`) }}
        </span>
        <span class="text-label-small text-n-slate-11">
          {{ t(`KANBAN.TEMPLATE_PICKER.${template.key}.DESCRIPTION`) }}
        </span>
      </button>
    </div>
    <Button
      v-if="applyingKey"
      is-loading
      slate
      faded
      :label="t('KANBAN.SETTINGS.LOADING')"
    />
  </div>
</template>
